import { gql } from '@apollo/client';
import LongReadPage from '~/components/LongRead/LongRead';
import ProductPage from '~/components/Product/ProductPage';
import config from '~/config';
import type { getEnergySavingSlugs_allEnergySavingPages } from '~/types/generated/cms/getEnergySavingSlugs';
import type { getFinancingSlugs_allFinancingPages } from '~/types/generated/cms/getFinancingSlugs';
import type {
  getLongReadOrProductBySlug,
  getLongReadOrProductBySlugVariables,
  getLongReadOrProductBySlug_allLongReads,
  getLongReadOrProductBySlug_allProductPages,
} from '~/types/generated/cms/getLongReadOrProductBySlug';
import type {
  getLongReadSlugs,
  getLongReadSlugs_allLongReads,
} from '~/types/generated/cms/getLongReadSlugs';
import type {
  getProductPageSlugs,
  getProductPageSlugs_allProductPages,
} from '~/types/generated/cms/getProductPageSlugs';
import {
  LongReadPageFragment,
  LongReadSlugsFragment,
  ProductPageFragment,
  ProductPageSlugsFragment,
} from 'src/datoCMS/fragments/woningVerduurzamenFragments';
import {
  BasicImageFragment,
  PageContentFragment,
  ProductHeroFragment,
  SeoMetaTagsFragment,
} from 'src/datoCMS/fragments/fragments';
import { getDatoClient } from 'src/datoCMS/datoCmsClient';
import type { ReactNode } from 'react';
import type { GetStaticPaths, GetStaticProps } from 'next';

const client = getDatoClient();

const GET_LONG_READ_OR_PRODUCT_BY_SLUG = gql`
  ${PageContentFragment}
  ${ProductHeroFragment}
  ${SeoMetaTagsFragment}
  ${BasicImageFragment}
  ${LongReadPageFragment}
  ${ProductPageFragment}
  query getLongReadOrProductBySlug($slug: String!) {
    allProductPages(filter: { slug: { eq: $slug } }) {
      ...ProductPageFragment
    }

    allLongReads(filter: { slug: { eq: $slug } }) {
      ...LongReadPageFragment
    }
  }
`;

const GET_PRODUCT_PAGE_SLUGS = gql`
  ${ProductPageSlugsFragment}
  query getProductPageSlugs {
    allProductPages {
      ...ProductPageSlugsFragment
    }
  }
`;

const GET_LONG_READ_SLUGS = gql`
  ${LongReadSlugsFragment}
  query getLongReadSlugs {
    allLongReads {
      ...LongReadSlugsFragment
    }
  }
`;

interface PageProps {
  page: getLongReadOrProductBySlug_allLongReads | getLongReadOrProductBySlug_allProductPages;
}

const Product: React.FC<PageProps> = ({ page }) => {
  const renderedPage: Record<'LongReadRecord' | 'ProductPageRecord', ReactNode> = {
    LongReadRecord: <LongReadPage page={page as getLongReadOrProductBySlug_allLongReads} />,
    ProductPageRecord: <ProductPage page={page as getLongReadOrProductBySlug_allProductPages} />,
  };

  return <>{renderedPage[page.__typename]}</>;
};

/**
 *
 * @param longReadSlugs The long-read pages for all categories (e.g. products, financing, energy-saving)
 * @param pageSlugs The pages for one of the categories that some of the long-reads belong to (e.g. products, financing, energy-saving)
 * @returns
 */
export const constructWoningVerduurzamenPageSlugs = (
  longReadSlugs: getLongReadSlugs_allLongReads[],
  pageSlugs:
    | getProductPageSlugs_allProductPages[]
    | getFinancingSlugs_allFinancingPages[]
    | getEnergySavingSlugs_allEnergySavingPages[],
) => {
  const constructedSlugs = [] as string[][];
  const categoryPageSlugs = pageSlugs.map(p =>
    p.parentPage?.slug ? [p.parentPage?.slug, p.slug!] : [p.slug!],
  );

  // First add all category pages, e.g. ['verwarming', 'cv-ketel'] (most are just one level deep)
  constructedSlugs.push(...categoryPageSlugs);

  // Then all individual articles (long reads), with their slug prefixed by their parent (category) page
  // E.g. ['producten', 'warmtepomp', 'wat-is-een-warmtepomp']
  for (const longReadSlug of longReadSlugs) {
    const parentSlugIndex = pageSlugs.findIndex(cat => cat.id === longReadSlug.parentPage?.id);
    if (parentSlugIndex >= 0) {
      const parentSlug = categoryPageSlugs[parentSlugIndex];
      constructedSlugs.push([...parentSlug, longReadSlug.slug!]);
    }
  }

  return constructedSlugs;
};

export const allProductPageSlugs = async () => {
  const { data } = await client.query<getProductPageSlugs>({
    query: GET_PRODUCT_PAGE_SLUGS,
  });

  return data.allProductPages;
};

export const allLongReadPageSlugs = async () => {
  const { data } = await client.query<getLongReadSlugs>({
    query: GET_LONG_READ_SLUGS,
  });

  return data.allLongReads;
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (config.build.skipStaticGeneration) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const productPageSlugs = await allProductPageSlugs();
  const longReadPageSlugs = await allLongReadPageSlugs();

  const pageSlugs = constructWoningVerduurzamenPageSlugs(
    [...longReadPageSlugs],
    [...productPageSlugs],
  );

  return {
    paths: pageSlugs.map(p => ({ params: { slug: p } })),
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<PageProps> = async ({ preview, params }) => {
  const datoClient = getDatoClient(preview);
  if (!params) return { notFound: true };

  const { data } = await datoClient.query<
    getLongReadOrProductBySlug,
    getLongReadOrProductBySlugVariables
  >({
    query: GET_LONG_READ_OR_PRODUCT_BY_SLUG,
    variables: { slug: params.slug ? params.slug[params.slug.length - 1] : '' },
  });

  const renderedPageData = data.allLongReads[0] || data.allProductPages[0];

  if (!renderedPageData) {
    return { notFound: true };
  }

  return {
    props: {
      page: renderedPageData,
    },
    revalidate: 60,
  };
};

export default Product;
